import { v4 } from 'uuid';
import { getCompanyOptionWithPriceSelect } from '@/api/get/config/get-company-options-with-price-select';
import addOptionVariantApi from '@/api/post/company/company-option/add-option-variant';
import Alert from '@/components/alert/alert-component';
import {
  processGalleryUpload,
  processThumbnailUpload,
} from '@/pages/company-management/company-options/options-utils';

/**
 * Get the selected option
 * @param {Array} options - The options
 * @param {string} linkId - The link id
 * @returns {object} The selected option
 */
export const getSelectedOption = (options, linkId) => {
  let retVal;
  options.every((option) => {
    if (option.prices) {
      retVal = option.prices.find((price) => price.id === linkId);
      if (retVal) {
        return false;
      }
    }
    return true;
  });
  return retVal;
};

/**
 * Process submit data for variant submission
 * @param {object} values - The form values
 * @param {string} optionId - The option id
 * @returns {Promise<object>} Processed variant data
 */
export const processSubmitData = async (values, optionId) => {
  const { variant, originalOption } = values;
  const { option_price_link_id: optionPriceLinkId } = originalOption;

  const {
    thumbnail,
    media,
    price_id: priceId,
    selectedPriceId,
    ...restOfValues
  } = variant;

  delete variant.hasProduct;
  delete variant.hasQuote;
  delete variant.temp_pn;
  delete variant.partNumber;

  // Use selectedPriceId if available, otherwise fall back to priceId
  const returnData = {
    ...restOfValues,
    price_id: selectedPriceId || priceId,
    parent_option: optionPriceLinkId,
    is_default: false,
  };

  // Handle file uploads for variant
  returnData.thumbnail = await processThumbnailUpload(thumbnail);
  returnData.media = await processGalleryUpload(media);

  // Filter out empty/null values to clean up API payload
  const processedVariant = Object.fromEntries(
    Object.entries(returnData).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== '' &&
        value !== '<p><br></p>', // Filter out empty rich text content
    ),
  );

  return { variant: processedVariant, optionId };
};

/**
 * Handles the addition of a new variant to a product option
 *
 * This function performs a complete workflow for adding a new variant:
 * 1. Processes the form data
 * 2. Creates the variant via API
 * 3. Refreshes the options data
 * 4. Appends the new variant to the form
 *
 * @async
 * @function handleAddNewVariant
 * @param {object} params - Parameters object
 * @param {object} params.values - Form values containing variant data (name, price, etc.)
 * @param {string} params.companyId - The ID of the company
 * @param {string} params.optionId - The ID of the option to add the variant to
 * @param {string} params.productId - The ID of the product
 * @param {Function} params.setOptions - State setter function to update options data
 * @param {Function} params.setLoading - State setter function to control loading state
 * @param {Function} params.setIsOpen - State setter function to control modal visibility
 * @param {Function} params.append - React Hook Form append function to add new form fields
 * @param {object} params.fieldToUpdate - The field to update
 * @returns {Promise<string|undefined>} Returns the created variant ID on success, undefined on error
 *
 * @throws {Error} Shows error alert if variant creation fails
 *
 * @example
 * const variantId = await handleAddNewVariant({
 *   values: formValues,
 *   companyId: 'company-123',
 *   optionId: 'option-456',
 *   productId: 'product-789',
 *   setOptions: setOptionsState,
 *   setLoading: setLoadingState,
 *   setIsOpen: setModalOpen,
 *   append: appendToForm
 * });
 */
export const handleAddNewVariant = async ({
  values,
  companyId,
  optionId,
  productId,
  setOptions,
  setLoading,
  setIsOpen,
  append,
  fieldToUpdate,
}) => {
  try {
    setLoading(true);

    // Step 1: Process submit data
    const submitValues = await processSubmitData(values, optionId);

    // Step 2: Create the variant
    const variant = await addOptionVariantApi(companyId, submitValues);

    // Step 3: Refetch options and wait for completion
    let updatedOptions = null;
    if (companyId && setOptions) {
      const response = await getCompanyOptionWithPriceSelect(companyId);
      setOptions(response.data);
      updatedOptions = response.data;
    }

    if (updatedOptions && variant) {
      // Step 4: Get the option from updated data and append
      const option = getSelectedOption(updatedOptions, variant.data.id);

      append({
        id: v4(),
        option_type: 'option',
        select_type: 'multi',
        is_required: false,
        children: [],
        name: option?.name,
        option_price_link_id: variant.data.id,
        dependencies: [],
        parent_id: fieldToUpdate?.id ?? null,
        isVariant: !!variant.data.parent_option,
        option_id: variant.data.option_id,
        product_id: productId,
        order: option.order,
      });
    }

    Alert({
      icon: 'success',
      text: 'Variant added successfully',
    });

    return variant.data.id;
  } finally {
    setLoading(false);
    setIsOpen(false);
  }
};

export default { getSelectedOption };
